import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'

import { AccountMapper } from '@/core/api/mappers/account/accountMapper'
import { accountService } from '@/core/api/services/account/accountService'
import { FULL_ACCOUNT_INCLUDES } from '@/core/api/services/account/constants'
import { RootState } from '@/core/store'
import { setSelectedAccount } from '@/core/store/slices/selectedAccountSlice'

import { useAuth } from '../useAuth'

/**
 * Hook to refetch current account data with full includes
 * This hook provides a consistent way to refresh account data across the app
 *
 * @returns A function to refetch account data (optionally with a specific accountId)
 */
export const useRefetchAccountData = () => {
	const dispatch = useDispatch()
	const queryClient = useQueryClient()
	const { refetchUser } = useAuth()
	const selectedAccount = useSelector(
		(state: RootState) => state.selectedAccount.selectedAccount
	)

	const refetchAccountData = useCallback(
		async (accountId?: number) => {
			const targetAccountId = accountId ?? selectedAccount?.id

			if (targetAccountId) {
				// Fetch full account details with all includes
				const response = await accountService.getAccount(targetAccountId, {
					params: {
						include: FULL_ACCOUNT_INCLUDES,
					},
				})

				const fullAccount = AccountMapper.toEntity(response.data.data)
				dispatch(setSelectedAccount(fullAccount))

				// Clear React Query cache to ensure all data is reloaded for the new account
				// This prevents stale data from being shown after account switching
				queryClient.clear()

				return fullAccount
			} else {
				// Fallback to refetching user data if no account is selected
				await refetchUser()
				return null
			}
		},
		[selectedAccount?.id, refetchUser, dispatch, queryClient]
	)

	return refetchAccountData
}
